import React, { useMemo } from "react";
import { Box, Typography, alpha } from "@mui/material";

/**
 * ONE canonical presentation of the real grade→subject mapping. Shared by
 * the schedule details drawer and the Assign Student modal so the two
 * cannot diverge. Never flattens grades × subjects into a single
 * sentence, never repeats the same subject inside one grade, never uses
 * "+N more" compression — every mapped subject is listed under its
 * actual grade.
 *
 * Rendered form (one row per grade):
 *   Grade 2  —  Science
 *   Grade 4  —  English, Science
 *   Grade 5  —  ABA Therapy, ABA/Academics
 *
 * Grades sort by the application's custom order (Pre-K, JK, SK, 1..12,
 * Adult); explicit `sort_order` fields from the backend take priority.
 * Subjects within a grade sort by their `sort_order` when present, else
 * alphabetically.
 *
 * Accepts the occurrence-details shape
 *   [{ grade: {id,name,sort_order}, subjects: [{id,name,sort_order}] }]
 * and the eligible-students shape
 *   [{ grade_id, grade: string, subjects: [{id,name}] }]
 * (legacy fallback: {grade:"Grade 1", subjects:["Math",...]}).
 */
export interface GSGroup {
  grade: { id?: number; name?: string; sort_order?: number | null } | string;
  grade_id?: number;
  subjects: Array<{ id?: number; name?: string; sort_order?: number | null } | string>;
}

const gradeName = (g: GSGroup["grade"]): string => (typeof g === "string" ? g : g?.name ?? "");
const gradeSortOrder = (g: GSGroup["grade"]): number | null =>
  typeof g === "string" ? null : (g?.sort_order ?? null);
const subjName = (s: any): string => (typeof s === "string" ? s : s?.name ?? "");
const subjId = (s: any): number | null => (typeof s === "string" ? null : (s?.id ?? null));
const subjSortOrder = (s: any): number | null =>
  typeof s === "string" ? null : (s?.sort_order ?? null);

// App-wide grade order for names that lack an explicit sort_order.
// Mirrors the order used elsewhere (Pre-K → 12 → Adult). Case-insensitive.
const CANONICAL_GRADE_ORDER = [
  "pre-k", "prek", "pre k",
  "jk", "junior kindergarten",
  "sk", "senior kindergarten",
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
  "adult",
] as const;
const canonicalGradeRank = (name: string): number => {
  const n = name.trim().toLowerCase();
  const direct = CANONICAL_GRADE_ORDER.indexOf(n as any);
  if (direct >= 0) return direct;
  // "Grade 4" / "grade 4" → strip prefix, retry as bare digit
  const stripped = n.replace(/^grade\s*/, "");
  const asIdx = CANONICAL_GRADE_ORDER.indexOf(stripped as any);
  return asIdx >= 0 ? asIdx : 999;
};

const GradeSubjectGroups: React.FC<{ groups?: GSGroup[] | null; dense?: boolean }> = ({ groups, dense }) => {
  const INDIGO = "#6366f1";

  // 1. Drop empty grades.
  // 2. De-duplicate subjects within a grade — by subject id where
  //    available, else by normalised name.
  // 3. Sort subjects within each grade by sort_order then name.
  // 4. Sort grade groups by explicit sort_order else canonical rank.
  const clean = useMemo(() => {
    const source = Array.isArray(groups) ? groups : [];
    const shaped = source
      .map((g) => {
        const seenIds = new Set<number>();
        const seenNames = new Set<string>();
        const subjects = (g.subjects || []).filter((s) => {
          const id = subjId(s);
          const name = subjName(s).trim();
          if (!name) return false;
          if (id != null) {
            if (seenIds.has(id)) return false;
            seenIds.add(id);
            return true;
          }
          const key = name.toLowerCase();
          if (seenNames.has(key)) return false;
          seenNames.add(key);
          return true;
        });
        subjects.sort((a, b) => {
          const oa = subjSortOrder(a);
          const ob = subjSortOrder(b);
          if (oa != null && ob != null && oa !== ob) return oa - ob;
          return subjName(a).localeCompare(subjName(b));
        });
        return { ...g, subjects };
      })
      .filter((g) => gradeName(g.grade));

    shaped.sort((a, b) => {
      const oa = gradeSortOrder(a.grade);
      const ob = gradeSortOrder(b.grade);
      if (oa != null && ob != null && oa !== ob) return oa - ob;
      return canonicalGradeRank(gradeName(a.grade)) - canonicalGradeRank(gradeName(b.grade));
    });
    return shaped;
  }, [groups]);

  if (clean.length === 0) {
    return (
      <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontStyle: "italic" }}>
        No grade-specific subjects configured.
      </Typography>
    );
  }

  // One row per grade — "Grade 2 — Science" text form as specced.
  // Every grade shown; no +N more compression.
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: dense ? 0.5 : 0.75,
        // Small pinned max-height with soft scroll for the rare
        // very-broad-class (6+ grades × many subjects) so the modal
        // itself doesn't grow past a reasonable viewport. Every row
        // remains reachable — no hidden "+more" affordance.
        maxHeight: dense ? 260 : 320,
        overflowY: "auto",
      }}
    >
      {clean.map((g, i) => {
        const gname = gradeName(g.grade);
        const label = /^(pre-?k|jk|sk|adult|\d+)$/i.test(gname.trim()) ? `Grade ${gname}` : gname;
        const subjectsText = g.subjects.map((s) => subjName(s)).join(", ");
        return (
          <Box
            key={typeof g.grade === "string" ? `${g.grade}-${i}` : String((g.grade as any)?.id ?? i)}
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1.25,
              px: dense ? 0.5 : 1,
              py: dense ? 0.35 : 0.6,
              borderRadius: "6px",
              "&:hover": { bgcolor: alpha(INDIGO, 0.04) },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: dense ? 11.5 : 12.5,
                fontWeight: 800,
                color: "#4338ca",
                minWidth: 62,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: dense ? 12 : 13,
                fontWeight: 500,
                color: "text.primary",
                lineHeight: 1.4,
              }}
            >
              {subjectsText || (
                <Box component="em" sx={{ color: "text.secondary" }}>No subjects</Box>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default GradeSubjectGroups;
